import { useState, useEffect } from 'react'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip'
import { AlertTriangle, Ban, HelpCircle, Clock, User, ExternalLink } from 'lucide-react'
import api from '../../lib/api'

interface FlaggedMessage {
  messageId: string
  content: string
  classification: 'spam' | 'off_topic' | 'suspicious'
  reason?: string
  classifiedAt: string
  timestamp: string
  sessionId: string
  visitorId: string
  pageUrl?: string
  visitorEmail?: string
  visitorName?: string
}

interface Summary {
  total: number
  byClassification: {
    spam: number
    off_topic: number
    suspicious: number
    legitimate: number
  }
  dailyBreakdown: Array<{
    date: string
    spam: number
    off_topic: number
    suspicious: number
  }>
}

interface FlaggedMessagesProps {
  timeframe: string
}

const classificationConfig = {
  spam: {
    label: 'Spam',
    icon: Ban,
    color: 'text-red-600',
    bgColor: 'bg-red-100',
    borderColor: 'border-red-200'
  },
  off_topic: {
    label: 'Off-Topic',
    icon: HelpCircle,
    color: 'text-amber-600',
    bgColor: 'bg-amber-100',
    borderColor: 'border-amber-200'
  },
  suspicious: {
    label: 'Suspicious',
    icon: AlertTriangle,
    color: 'text-orange-600',
    bgColor: 'bg-orange-100',
    borderColor: 'border-orange-200'
  }
}

export default function FlaggedMessages({ timeframe }: FlaggedMessagesProps) {
  const [messages, setMessages] = useState<FlaggedMessage[]>([])
  const [summary, setSummary] = useState<Summary | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    loadFlaggedMessages()
  }, [timeframe])

  const loadFlaggedMessages = async () => {
    setLoading(true)
    setError(null)
    try {
      const response = await api.get(`/api/analytics/flagged-messages?timeframe=${timeframe}&limit=20`)
      if (response.data.success) {
        setMessages(response.data.flaggedMessages)
        setSummary(response.data.summary)
      }
    } catch (err: any) {
      console.error('Failed to load flagged messages:', err)
      setError('Failed to load flagged messages')
    } finally {
      setLoading(false)
    }
  }

  if (loading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <AlertTriangle className="h-5 w-5 text-amber-500" />
            Flagged Messages
          </CardTitle>
          <CardDescription>Messages flagged by the AI classifier</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {[...Array(3)].map((_, i) => (
              <div key={i} className="animate-pulse">
                <div className="h-20 bg-muted rounded" />
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    )
  }

  if (error) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <AlertTriangle className="h-5 w-5 text-amber-500" />
            Flagged Messages
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-center py-8 text-muted-foreground text-sm">
            {error}
          </div>
        </CardContent>
      </Card>
    )
  }

  const formatTime = (timestamp: string) => {
    const date = new Date(timestamp)
    const now = new Date()
    const diff = now.getTime() - date.getTime()
    const hours = Math.floor(diff / (1000 * 60 * 60))
    const days = Math.floor(hours / 24)

    if (days > 0) return `${days}d ago`
    if (hours > 0) return `${hours}h ago`
    return 'Just now'
  }

  return (
    <TooltipProvider>
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <AlertTriangle className="h-5 w-5 text-amber-500" />
            Flagged Messages
          </CardTitle>
          <CardDescription>
            Messages flagged as spam, off-topic, or suspicious by the AI classifier
          </CardDescription>
        </CardHeader>
        <CardContent>
          {/* Summary Stats */}
          {summary && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4 mb-6">
              <div className="text-center p-2 md:p-3 bg-muted/50 rounded-lg">
                <div className="text-xl md:text-2xl font-bold">{summary.total}</div>
                <div className="text-xs text-muted-foreground">Total Flagged</div>
              </div>
              <div className="text-center p-2 md:p-3 bg-red-50 rounded-lg border border-red-100">
                <div className="text-xl md:text-2xl font-bold text-red-600">
                  {summary.byClassification.spam}
                </div>
                <div className="text-xs text-red-600">Spam</div>
              </div>
              <div className="text-center p-2 md:p-3 bg-amber-50 rounded-lg border border-amber-100">
                <div className="text-xl md:text-2xl font-bold text-amber-600">
                  {summary.byClassification.off_topic}
                </div>
                <div className="text-xs text-amber-600">Off-Topic</div>
              </div>
              <div className="text-center p-2 md:p-3 bg-orange-50 rounded-lg border border-orange-100">
                <div className="text-xl md:text-2xl font-bold text-orange-600">
                  {summary.byClassification.suspicious}
                </div>
                <div className="text-xs text-orange-600">Suspicious</div>
              </div>
            </div>
          )}

          {/* Message List */}
          {messages.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground text-sm">
              <AlertTriangle className="h-8 w-8 mx-auto mb-2 opacity-50" />
              No flagged messages in this time period.
              <br />
              <span className="text-xs">This is a good sign!</span>
            </div>
          ) : (
            <div className="space-y-3 max-h-[400px] overflow-y-auto">
              {messages.map((msg) => {
                const config = classificationConfig[msg.classification]
                const Icon = config.icon

                return (
                  <div
                    key={msg.messageId}
                    className={`p-3 rounded-lg border ${config.borderColor} ${config.bgColor}/30`}
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex-1 min-w-0">
                        {/* Classification Badge */}
                        <div className="flex items-center gap-2 mb-2">
                          <Badge
                            variant="outline"
                            className={`${config.color} ${config.bgColor} border-0`}
                          >
                            <Icon className="h-3 w-3 mr-1" />
                            {config.label}
                          </Badge>
                          <span className="text-xs text-muted-foreground flex items-center gap-1">
                            <Clock className="h-3 w-3" />
                            {formatTime(msg.classifiedAt)}
                          </span>
                        </div>

                        {/* Message Content */}
                        <p className="text-sm text-foreground mb-2 line-clamp-2">
                          "{msg.content}"
                        </p>

                        {/* Reason */}
                        {msg.reason && (
                          <p className="text-xs text-muted-foreground italic mb-2">
                            Reason: {msg.reason}
                          </p>
                        )}

                        {/* Visitor Info */}
                        <div className="flex items-center gap-3 text-xs text-muted-foreground">
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <span className="flex items-center gap-1 cursor-help">
                                <User className="h-3 w-3" />
                                {msg.visitorName || msg.visitorEmail || 'Anonymous'}
                              </span>
                            </TooltipTrigger>
                            <TooltipContent>
                              <div>
                                <div>Visitor ID: {msg.visitorId.slice(0, 8)}...</div>
                                {msg.visitorEmail && <div>Email: {msg.visitorEmail}</div>}
                              </div>
                            </TooltipContent>
                          </Tooltip>

                          {msg.pageUrl && (
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <span className="flex items-center gap-1 cursor-help truncate max-w-[150px]">
                                  <ExternalLink className="h-3 w-3 flex-shrink-0" />
                                  {new URL(msg.pageUrl).pathname || '/'}
                                </span>
                              </TooltipTrigger>
                              <TooltipContent>
                                <div className="text-xs">{msg.pageUrl}</div>
                              </TooltipContent>
                            </Tooltip>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                )
              })}
            </div>
          )}

          {/* Insight */}
          {summary && summary.total > 0 && (
            <div className="mt-4 p-3 bg-muted/50 rounded-lg">
              <p className="text-sm text-muted-foreground">
                <strong className="text-foreground">💡 Note:</strong> These messages were automatically
                flagged by the AI classifier. Review them periodically to ensure quality and catch any
                patterns that might indicate system issues or bad actors.
              </p>
            </div>
          )}
        </CardContent>
      </Card>
    </TooltipProvider>
  )
}
